import {
  Routes,
  Route,
  useNavigate,
  useLocation,
  useParams,
  Navigate,
} from "react-router";
import Welcome from "./routes/welcome";
import WelcomeConnexion from "./routes/welcome.connexion";
import WelcomeLoginRedirect from "./routes/welcome.login-redirect";
import { refreshUser, useMostFreshUser } from "./utils/use-most-fresh-user";
import { useAppVersion } from "./utils/use-app-version";
import { usePreviewView } from "./zustand/preview-view";
import { isDemoEmail } from "./utils/demo-account";
import { ReactElement, useEffect, useState } from "react";
// import { isUserOnboarded } from "./utils/onboarding";
import WelcomeLoginProfile from "./routes/welcome.login-profile";
import CguAcceptanceModal from "./components/CguAcceptanceModal";
import AuthSetupWizard from "./components/AuthSetupWizard";
import { readAuthPolicy } from "./utils/auth-policy";
import CGUS from "./routes/cgus";
import CompteRenduLien from "./routes/compte-rendu.$jeton";
import PrivacyPolicy from "./routes/confidentialité";
import { Toaster } from "./components/ui/sonner";
import HomeLayout from "./routes/home.layout";
import Dashboard from "./routes/dashboard";
import Patient from "./routes/patient";
import PatientDocument from "./routes/patient.document.$docId";
import TeleExpertise from "./routes/tele-expertise";
import TeleExpertiseDossier from "./routes/tele-expertise.$dossierId";
import Configuration from "./routes/configuration";
import Datasets from "./routes/datasets";
import DatasetDetail from "./routes/dataset.$id";
import DatasetNew from "./routes/dataset.new";
import ExperimentNew from "./routes/dataset.$id.experiments.new";
import ExperimentDetail from "./routes/dataset.$id.experiment.$expId";
import ExperimentCompare from "./routes/dataset.$id.experiments.compare";
import Catalogue from "./routes/catalogue";
import Etudes from "./routes/etudes";
import Laboratoire from "./routes/laboratoire";
import LaboratoireOntologie from "./routes/laboratoire.ontologie";
import LaboratoireMetriques from "./routes/laboratoire.metriques";
import LaboratoireLlm from "./routes/laboratoire.llm";
import LaboratoireCorrections from "./routes/laboratoire.corrections";
import { isInspectorRole } from "@app/utils/inspector-role";
import { useIsReviewer } from "@app/utils/use-is-reviewer";

function App() {
  useAppVersion(); // set data-mage-x-*-version on <html> for inspector
  useEffect(() => {
    refreshUser("App");
  }, []);

  return (
    <>
      <Toaster position="bottom-right" />
      <Routes>
        <Route path="health" element={<div>OK</div>} />
        <Route path="cgus" element={<CGUS />} />
        {/* Outside the application on purpose: a patient and their referring physician have
            no account, and this page is the only thing they ever see of the platform. */}
        <Route path="compte-rendu/:jeton" element={<CompteRenduLien />} />
        <Route path="confidentialité" element={<PrivacyPolicy />} />
        <Route
          path="welcome/early"
          element={<Navigate to="/welcome?acces=1" replace />}
        />
        <Route
          path="welcome/earlyuser"
          element={<Navigate to="/welcome?acces=1" replace />}
        />
        <Route
          path="early-user"
          element={<Navigate to="/welcome?acces=1" replace />}
        />
        <Route path="welcome">
          <Route element={<Welcome />}>
            <Route index element={<WelcomeConnexion />} />
            <Route path="login-redirect" element={<WelcomeLoginRedirect />} />
            <Route path="login-profile" element={<WelcomeLoginProfile />} />
          </Route>
        </Route>
        <Route
          element={
            <RestrictedRoute id="app">
              <HomeLayout />
            </RestrictedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="patient" element={<Patient />} />
          <Route path="patient/:tenant_id/:id_patient" element={<Patient />} />
          <Route
            path="patient/:tenant_id/:id_patient/documents/:doc_id"
            element={<PatientDocument />}
          />
          <Route
            path="temporal-patient"
            element={<Navigate to="/patient" replace />}
          />
          <Route
            path="temporal-patient/:tenant_id/:id_patient"
            element={<LegacyPatientRedirect />}
          />
          <Route path="tele-expertise" element={<TeleExpertise />} />
          <Route
            path="tele-expertise/:dossierId"
            element={<TeleExpertiseDossier />}
          />
          <Route path="configuration" element={<Configuration />} />
          <Route path="catalogue" element={<Catalogue />} />
          <Route path="etudes" element={<Etudes />} />
          <Route path="laboratoire" element={<Laboratoire />} />
          <Route
            path="laboratoire/ontologie"
            element={<LaboratoireOntologie />}
          />
          <Route
            path="laboratoire/metriques"
            element={<LaboratoireMetriques />}
          />
          <Route path="laboratoire/llm" element={<LaboratoireLlm />} />
          <Route
            path="laboratoire/corrections"
            element={<LaboratoireCorrections />}
          />
          <Route path="datasets" element={<Datasets />} />
          <Route path="datasets/new" element={<DatasetNew />} />
          <Route path="datasets/:id" element={<DatasetDetail />} />
          <Route
            path="datasets/:id/experiments/new"
            element={<ExperimentNew />}
          />
          <Route
            path="datasets/:id/experiments/compare"
            element={<ExperimentCompare />}
          />
          <Route
            path="datasets/:id/experiments/:expId"
            element={<ExperimentDetail />}
          />
        </Route>
      </Routes>
    </>
  );
}

/** Old links to `/temporal-patient/...` (bookmarks, e-mails) land on the patient page, date kept. */
function LegacyPatientRedirect() {
  const { tenant_id, id_patient } = useParams();
  const { search } = useLocation();
  return (
    <Navigate to={`/patient/${tenant_id}/${id_patient}${search}`} replace />
  );
}

function RestrictedRoute({
  children,
  id,
}: {
  children: ReactElement;
  id: string;
}) {
  const user = useMostFreshUser("RestrictedRoute " + id);
  const navigate = useNavigate();
  const location = useLocation();
  const previewRole = usePreviewView((state) => state.previewRole);
  const effectiveRole = previewRole ?? (user as { role?: string })?.role;
  const isReviewer = useIsReviewer();
  // The cached user is only a cache: a full-page arrival with a valid cookie and an empty cache
  // (Pro Santé Connect's callback after a sign-out) asks the server before leaving for /welcome.
  const [sessionChecked, setSessionChecked] = useState(Boolean(user?.id));

  useEffect(() => {
    if (user?.id || sessionChecked) return;
    let cancelled = false;
    void refreshUser("RestrictedRoute " + id).finally(() => {
      if (!cancelled) setSessionChecked(true);
    });
    return () => {
      cancelled = true;
    };
  }, [user?.id, sessionChecked, id]);

  useEffect(() => {
    if (!user?.id) {
      if (sessionChecked) navigate("/welcome");
      return;
    }
    // A doctor has no laboratory; the demo reads its worked example there (PT-105), and MAGE-X's
    // reviewers keep the fondateur's (PT-119) — the sidebar offers it to them, so must the route.
    if (
      effectiveRole === "medecin" &&
      !isReviewer &&
      !isDemoEmail((user as { email?: string }).email) &&
      (location.pathname.startsWith("/datasets") ||
        location.pathname.startsWith("/laboratoire"))
    ) {
      navigate("/dashboard");
      return;
    }
    // An inspector (global persona, manufacturer's reader) reaches the dashboard, the catalogue, the
    // studies and the configuration only.
    if (isInspectorRole(effectiveRole)) {
      const allowed =
        location.pathname === "/" ||
        location.pathname.startsWith("/dashboard") ||
        location.pathname.startsWith("/catalogue") ||
        location.pathname.startsWith("/etudes") ||
        location.pathname.startsWith("/configuration") ||
        location.pathname.startsWith("/welcome");
      if (!allowed) {
        navigate("/dashboard");
        return;
      }
    }
  }, [user, navigate, location, effectiveRole, isReviewer, sessionChecked]);

  if (!user?.id) {
    return <div>Chargement...</div>;
  }

  // An enrollment session: the server already refuses every application call, so the
  // app would render as a run of errors. Show instead the one screen this session can
  // serve — and the only one that leads out of it. Rendered here rather than in
  // HomeLayout, which would fire mount-time loads that would all fail.
  const authPolicy = readAuthPolicy(user);
  if (authPolicy?.state === "required") {
    return <AuthSetupWizard policy={authPolicy} />;
  }

  const hasCguAccepted = !!(user as { cgu_accepted_at?: string | null })
    .cgu_accepted_at;

  return (
    <>
      {!hasCguAccepted && <CguAcceptanceModal />}
      {children}
    </>
  );
}

export default App;
